import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { NumInput as InputNumber } from "../components/NumInput";
import type { ColumnsType } from "antd/es/table";

import {
  ApiError,
  type BackupConfigRow,
  type BackupFileRow,
  api,
} from "../api/client";

const { Title } = Typography;

const formatBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export const BackupsPage = () => {
  const [config, setConfig] = useState<BackupConfigRow | null>(null);
  const [files, setFiles] = useState<BackupFileRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<BackupFileRow | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [form] = Form.useForm<BackupConfigRow>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, list] = await Promise.all([
        api.getBackupConfig(),
        api.listBackups(),
      ]);
      setConfig(cfg);
      setFiles(list);
      form.setFieldsValue(cfg);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaveConfig = async () => {
    try {
      const values = await form.validateFields();
      await api.updateBackupConfig(values);
      setEditing(false);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  };

  const onRunNow = async () => {
    setInfo(null);
    setError(null);
    try {
      const r = await api.runBackupNow();
      setInfo(
        `Backup ok: ${r.filename} (${formatBytes(r.size_bytes)}; purged ${r.purged_files.length})`,
      );
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const onConfirmRestore = async () => {
    if (!restoreTarget || restoreConfirm !== "RESTORE") return;
    try {
      await api.restoreBackup(restoreTarget.filename);
      setInfo(
        `Restore started for ${restoreTarget.filename}. Admin will restart shortly.`,
      );
      setRestoreTarget(null);
      setRestoreConfirm("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const columns: ColumnsType<BackupFileRow> = [
    { title: "Filename", dataIndex: "filename" },
    {
      title: "Size",
      dataIndex: "size_bytes",
      align: "right" as const,
      render: (v: number) => formatBytes(v),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: "SHA256",
      dataIndex: "sha256",
      render: (v: string) => (
        <code style={{ fontSize: 11 }}>{v.slice(0, 12)}…</code>
      ),
    },
    {
      title: "Actions",
      render: (_: unknown, row) => (
        <Space>
          {row.download_url ? (
            <Button size="small" href={row.download_url} target="_blank">
              Download
            </Button>
          ) : null}
          <Button
            size="small"
            danger
            onClick={() => {
              setRestoreTarget(row);
              setRestoreConfirm("");
            }}
          >
            Restore
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card loading={loading}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>
          Backups
        </Title>
        <Space>
          <Button onClick={onRunNow} type="primary">
            Run backup now
          </Button>
          <Button onClick={() => setEditing(true)}>Edit schedule</Button>
        </Space>
      </Space>
      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}
      {info && (
        <Alert
          type="success"
          message={info}
          style={{ marginBottom: 16 }}
          showIcon
          closable
          onClose={() => setInfo(null)}
        />
      )}
      {config && (
        <Descriptions
          column={2}
          size="small"
          style={{ marginBottom: 24 }}
          bordered
        >
          <Descriptions.Item label="Enabled">
            {config.enabled ? (
              <Tag color="green">on</Tag>
            ) : (
              <Tag color="default">paused</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Schedule">
            <code>{config.schedule_cron}</code>
          </Descriptions.Item>
          <Descriptions.Item label="Target">
            <code>{config.target_path}</code>
          </Descriptions.Item>
          <Descriptions.Item label="Retention (H/D/W)">
            {config.retention_hourly} / {config.retention_daily} /{" "}
            {config.retention_weekly}
          </Descriptions.Item>
          <Descriptions.Item label="Last run">
            {config.last_run ? new Date(config.last_run).toLocaleString() : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Last status">
            {config.last_status ?? "—"}
          </Descriptions.Item>
        </Descriptions>
      )}
      <Table<BackupFileRow>
        columns={columns}
        dataSource={files}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25 }}
      />
      <Modal
        title="Edit backup config"
        open={editing}
        onOk={onSaveConfig}
        onCancel={() => setEditing(false)}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Cron schedule" name="schedule_cron">
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item label="Hourly retention" name="retention_hourly">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Daily retention" name="retention_daily">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Weekly retention" name="retention_weekly">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Target path" name="target_path">
            <Input maxLength={512} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`Restore ${restoreTarget?.filename}`}
        open={restoreTarget !== null}
        onOk={onConfirmRestore}
        okButtonProps={{
          danger: true,
          disabled: restoreConfirm !== "RESTORE",
        }}
        okText="Restore (destructive)"
        onCancel={() => {
          setRestoreTarget(null);
          setRestoreConfirm("");
        }}
      >
        <Alert
          type="warning"
          showIcon
          message="This will replace both databases with the contents of the selected backup, then restart the admin container. The action is irreversible."
          style={{ marginBottom: 16 }}
        />
        <p>
          Type <code>RESTORE</code> to confirm:
        </p>
        <Input
          value={restoreConfirm}
          onChange={(e) => setRestoreConfirm(e.target.value)}
          placeholder="RESTORE"
        />
      </Modal>
    </Card>
  );
};
